import {
  createRouter,
  createRootRoute,
  createRoute,
  createBrowserHistory,
  redirect,
} from '@tanstack/react-router';
import { RootLayout } from '../components/RootLayout';
import BoardV3Page from '../boardv3/pages/BoardV3Page';
import { OverviewPage } from '../pages/OverviewPage';
import { SkillsPage } from '../pages/SkillsPage';
import { TeamsPage } from '../teams/TeamsPage';
import { EmployeesPage } from '../employees/EmployeesPage';
import { ColumnsPage } from '../pages/ColumnsPage';
import { BoardsPage } from '../pages/BoardsPage';
import { GlobalTasksPage } from '../pages/GlobalTasksPage';
import { CategoriesPage } from '../taskCategories/CategoriesPage';
import { AdrsPage } from '../adr/AdrsPage';
import { ResourcesPage } from '../resources/ResourcesPage';
import { BoardLayout } from '../boardv3/pages/BoardLayout';
import { CanvasPage } from '../canvas';
import { EditorPage } from '../editor';
import { KickoffPage } from '../kickoff/KickoffPage';
import { LandingPage } from '../landing/LandingPage';
import { ForProjektmanagerPage } from '../landing/audience/ForProjektmanagerPage';
import { ForEngineeringManagerPage } from '../landing/audience/ForEngineeringManagerPage';
import { ForEngineerPage } from '../landing/audience/ForEngineerPage';
import { ForFreelancerPage } from '../landing/audience/ForFreelancerPage';
import { ForPlatformEngineerPage } from '../landing/audience/ForPlatformEngineerPage';
import { DiscoveryPage } from '../landing/features/DiscoveryPage';
import { PertPage } from '../landing/features/PertPage';
import { SquadFormingPage } from '../landing/features/SquadFormingPage';
import { AdrFeaturePage } from '../landing/features/AdrFeaturePage';
import { ResourcesFeaturePage } from '../landing/features/ResourcesFeaturePage';
import { NoPricingPage } from '../landing/pricing/NoPricingPage';
import { ImpressumPage } from '../landing/legal/ImpressumPage';

// Search param types
export type BoardSearchParams = {
  taskId?: string;
  zoom?: number;
};

export type OverviewSearchParams = {
  view?: 'columns' | 'timeline';
  anchor?: string;
};

export type SkillsSearchParams = {
  skillId?: string;
  employee?: string;
};

// Root route
const rootRoute = createRootRoute({
  component: RootLayout,
});

// Index route - Landing Page
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

// Boards list page
const boardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boards',
  component: BoardsPage,
});

// Board layout route (wrapper with ActiveBoardProvider)
const boardLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/boards/$boardId',
  component: BoardLayout,
});

// Board-specific routes (nested under boardLayoutRoute)
const boardRoadmapRoute = createRoute({
  getParentRoute: () => boardLayoutRoute,
  path: '/roadmap',
  component: BoardV3Page,
  validateSearch: (search: Record<string, unknown>): BoardSearchParams => ({
    taskId: search.taskId as string | undefined,
    zoom: search.zoom ? Number(search.zoom) : undefined,
  }),
});

const boardOverviewRoute = createRoute({
  getParentRoute: () => boardLayoutRoute,
  path: '/overview',
  component: OverviewPage,
  validateSearch: (search: Record<string, unknown>): OverviewSearchParams => ({
    view: (search.view as 'columns' | 'timeline') || undefined,
    anchor: search.anchor as string | undefined,
  }),
});

const boardColumnsRoute = createRoute({
  getParentRoute: () => boardLayoutRoute,
  path: '/columns',
  component: ColumnsPage,
});

const boardCanvasRoute = createRoute({
  getParentRoute: () => boardLayoutRoute,
  path: '/canvas',
  component: CanvasPage,
});

const boardEditorRoute = createRoute({
  getParentRoute: () => boardLayoutRoute,
  path: '/editor',
  component: EditorPage,
});

// Board index - redirect to roadmap
const boardIndexRoute = createRoute({
  getParentRoute: () => boardLayoutRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/boards/$boardId/roadmap', params: { boardId: params.boardId } });
  },
});

// Global routes (not board-specific)
const skillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/skills',
  component: SkillsPage,
  validateSearch: (search: Record<string, unknown>): SkillsSearchParams => ({
    skillId: search.skillId as string | undefined,
    employee: search.employee as string | undefined,
  }),
});

const teamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/teams',
  component: TeamsPage,
});

const employeesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees',
  component: EmployeesPage,
});

const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tasks',
  component: GlobalTasksPage,
});

const categoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/categories',
  component: CategoriesPage,
});

const adrsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/adrs',
  component: AdrsPage,
});

const resourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/resources',
  component: ResourcesPage,
});

const kickoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kickoff',
  component: KickoffPage,
});

// Audience landing pages — DE
const forDePmRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/for/projektmanager', component: () => ForProjektmanagerPage({ lang: 'de' }) });
const forDeEmRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/for/engineering-manager', component: () => ForEngineeringManagerPage({ lang: 'de' }) });
const forDeEngRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/for/engineer', component: () => ForEngineerPage({ lang: 'de' }) });
const forDeFreelancerRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/for/freelancer', component: () => ForFreelancerPage({ lang: 'de' }) });
const forDePlatformRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/for/platform-engineer', component: () => ForPlatformEngineerPage({ lang: 'de' }) });

// Audience landing pages — EN
const forEnPmRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/for/projektmanager', component: () => ForProjektmanagerPage({ lang: 'en' }) });
const forEnEmRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/for/engineering-manager', component: () => ForEngineeringManagerPage({ lang: 'en' }) });
const forEnEngRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/for/engineer', component: () => ForEngineerPage({ lang: 'en' }) });
const forEnFreelancerRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/for/freelancer', component: () => ForFreelancerPage({ lang: 'en' }) });
const forEnPlatformRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/for/platform-engineer', component: () => ForPlatformEngineerPage({ lang: 'en' }) });

// Feature pages — DE
const featDeDiscoveryRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/features/discovery', component: () => DiscoveryPage({ lang: 'de' }) });
const featDePertRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/features/pert', component: () => PertPage({ lang: 'de' }) });
const featDeSquadRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/features/squad-forming', component: () => SquadFormingPage({ lang: 'de' }) });
const featDeAdrRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/features/adrs', component: () => AdrFeaturePage({ lang: 'de' }) });
const featDeResourcesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/features/resources', component: () => ResourcesFeaturePage({ lang: 'de' }) });

// Feature pages — EN
const featEnDiscoveryRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/features/discovery', component: () => DiscoveryPage({ lang: 'en' }) });
const featEnPertRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/features/pert', component: () => PertPage({ lang: 'en' }) });
const featEnSquadRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/features/squad-forming', component: () => SquadFormingPage({ lang: 'en' }) });
const featEnAdrRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/features/adrs', component: () => AdrFeaturePage({ lang: 'en' }) });
const featEnResourcesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/features/resources', component: () => ResourcesFeaturePage({ lang: 'en' }) });

// Pricing page
const pricingDeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/pricing', component: () => NoPricingPage({ lang: 'de' }) });
const pricingEnRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/pricing', component: () => NoPricingPage({ lang: 'en' }) });

// Legal pages
const impressumDeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/de/legal/impressum', component: () => ImpressumPage({ lang: 'de' }) });
const impressumEnRoute = createRoute({ getParentRoute: () => rootRoute, path: '/en/legal/impressum', component: () => ImpressumPage({ lang: 'en' }) });

// Route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  forDePmRoute, forDeEmRoute, forDeEngRoute, forDeFreelancerRoute, forDePlatformRoute,
  forEnPmRoute, forEnEmRoute, forEnEngRoute, forEnFreelancerRoute, forEnPlatformRoute,
  featDeDiscoveryRoute, featDePertRoute, featDeSquadRoute, featDeAdrRoute, featDeResourcesRoute,
  featEnDiscoveryRoute, featEnPertRoute, featEnSquadRoute, featEnAdrRoute, featEnResourcesRoute,
  pricingDeRoute, pricingEnRoute,
  impressumDeRoute, impressumEnRoute,
  boardsRoute,
  boardLayoutRoute.addChildren([
    boardIndexRoute,
    boardRoadmapRoute,
    boardOverviewRoute,
    boardColumnsRoute,
    boardCanvasRoute,
    boardEditorRoute,
  ]),
  skillsRoute,
  teamsRoute,
  employeesRoute,
  tasksRoute,
  categoriesRoute,
  adrsRoute,
  resourcesRoute,
  kickoffRoute,
]);

// Create browser history for clean URL routing (e.g., /boards/default/roadmap)
const browserHistory = createBrowserHistory();

// Create router instance
export const router = createRouter({
  routeTree,
  history: browserHistory,
  defaultPreload: 'intent',
});

// Type declarations for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
